import React, { PropsWithChildren, useState, useEffect } from 'react';
import {
    MappedComponentProperties,
} from '@adobe/aem-react-editable-components';

import './PageSection.css';
import { Model } from '@adobe/aem-spa-page-model-manager';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useWidthContext } from '../../providers/Width';
import withAsyncImport from '../../withAsyncImport';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Row, Title, Section } from '../../importCL';

const CobGrid = withAsyncImport(() => import('../CobGrid'));

interface IPageSectionProps extends MappedComponentProperties {
    anchorId?: string;
    cqItems?: any;
    cqItemsOrder?: string[];
    gridClassNames?: any;
    numCols?: number;
    title?: string;
    columnClasses?: string[];
    titleVariant?: 'center' | 'left';
    titleSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
    subtitle?: string;
    backgroundColor?: string;
    backgroundImage?: string;
    mobileBackgroundImage?: string;
    sectionLinkLabel?: string;
    sectionLink?: string;
    target?: '_blank';
    backgroundOverlay?: boolean;
    hideOnDesktop?: boolean;
    hideOnMobile?: boolean;
    singleColumnMobile?: boolean;
    enablePrint?: boolean;
    desktopTopPadding?: string;
    desktopBottomPadding?: string;
    mobileTopPadding?: string;
    mobileBottomPadding?: string;
    firstColumnLeftPadding?: string;
    firstColumnRightPadding?: string;
    columnDesktopLeftPadding?: string;
    columnDesktopRightPadding?: string;
    columnMobileLeftPadding?: string;
    columnMobileRightPadding?: string;
    removeTopPadding?: boolean;
    removeBotPadding?: boolean;
    showHr?: boolean;
    topBorder?: 'solid-full-width' | 'solid-centered' | 'dotted-full-width' | 'dotted-centered';
    disclaimer?: string;
    ctaStyle?: 'solid' | 'ghosted-light' | 'ghosted-dark' | 'link';
    showChevron?: boolean;
    columnStack?: string;
    preloadImage?: boolean;
}

export const PageSectionEditConfig = {
    emptyLabel: 'PageSection',
    isEmpty: (props: PropsWithChildren<IPageSectionProps>) => {
        return !props || !props.cqItemsOrder || !props.cqItemsOrder.length;
    },
};

const transformCqProps = (propKey: string): string => {
    return `cq${propKey.substring(1, 2).toUpperCase()}${propKey.substring(2)}`;
};

const modelToProps = (item: Model) => {
    const keys = Object.getOwnPropertyNames(item);
    const props: any = {};

    keys.forEach((key: string) => {
        let propKey: string = key;

        if (propKey.startsWith(':')) {
            propKey = transformCqProps(propKey);
        }
        // @ts-ignore
        props[propKey] = item[key];
    });

    return props;
};

export const getSecChildren = (props: PropsWithChildren<IPageSectionProps>, key: string): any => {
    const children: any = {};

    if (!props.cqItems || !props.cqItems[key] || !props.cqItems[key][':items'] || !props.cqItems[key][':itemsOrder']) {
        return children;
    }

    props.cqItems[key][':itemsOrder'].map((childKey: string) => {
        const childProps = modelToProps(props.cqItems[key][':items'][childKey]);

        children[childKey] = childProps;
    });
    return children;
};

const PageSection = (props: PropsWithChildren<IPageSectionProps>): JSX.Element => {
    const {titleSize = 'h2', titleVariant = 'center' } = props;
    const { width } = useWidthContext();
    const [authoredStyles, setAuthoredStyles] = useState({
        section: {},
        column: {},
        firstColumn: {},
        mobileSection: {},
        mobileColumn: {},
    });

    useEffect(() => {
        setAuthoredStyles({
            section: {
                paddingTop: (props.removeTopPadding && '0') || props.desktopTopPadding || '',
                paddingBottom: (props.removeBotPadding && '0') || props.desktopBottomPadding || '',
            },
            column: {
                paddingLeft: props.columnDesktopLeftPadding || '',
                paddingRight: props.columnDesktopRightPadding || '',
            },
            firstColumn: {
                paddingLeft: props.firstColumnLeftPadding || '',
                paddingRight: props.firstColumnRightPadding || '',
            },
            mobileSection: {
                paddingTop: (props.removeTopPadding && '0') || props.mobileTopPadding || props.desktopTopPadding || '',
                paddingBottom: (props.removeBotPadding && '0') || props.mobileBottomPadding || props.desktopBottomPadding  || '',
            },
            mobileColumn: {
                paddingLeft: props.columnMobileLeftPadding || props.columnDesktopLeftPadding || '',
                paddingRight: props.columnMobileRightPadding || props.columnDesktopRightPadding || '',
            },
        });
    }, []);

    function getAuthoredColumnStyle() {
        if (width < 768) {
            return authoredStyles.mobileColumn;
        }
        return authoredStyles.column;
    }
    const darkMode = props.backgroundColor === 'blue-denim' || props.backgroundColor === 'gradient' || props.backgroundColor === 'blue' || props.backgroundColor === 'purple' || props.backgroundColor === 'blue-fdic'|| props.backgroundColor === 'navy-blue';
    let backgroundColor: 'light-grey' | 'blue-denim' | 'white' | 'gradient' | undefined | 'purple' | 'blue' | 'blue-fdic' | 'light-blue';
    if (props.backgroundColor === 'light-grey' || props.backgroundColor === 'light-blue' || props.backgroundColor === 'blue-denim' || props.backgroundColor === 'white' || props.backgroundColor === 'gradient' || props.backgroundColor === undefined || props.backgroundColor === 'purple' || props.backgroundColor === 'blue' || props.backgroundColor === 'blue-fdic') {
        backgroundColor = props.backgroundColor as 'light-grey' | 'blue-denim' | 'white' | 'gradient' | undefined | 'purple' | 'blue' | 'blue-fdic' | 'light-blue';
    }

    let itemsOrder = props.cqItemsOrder;
    if (itemsOrder && ((props.columnStack === '2alt2reverse' && width <= 768))) {
        itemsOrder = [...itemsOrder].reverse();
    }

    return (
        <React.Fragment>
            {props.anchorId && (<a id={props.anchorId} className='anchor-section'></a>)}
            {props.topBorder && (<div className={`top-border--${props.topBorder}`}></div>)}
            {props.preloadImage && props.backgroundImage && <img loading={props.preloadImage ? 'eager' : 'lazy'} className='hidden' src={props.backgroundImage} />}
            {(!props.hideOnMobile || width > 768 || props.isInEditor) && (
                <Section
                    id='page-section'
                    className={`PageSection 
                            ${props.hideOnDesktop && !props.isInEditor ? 'hideDesktop' : ''}
                            ${props.backgroundColor === 'navy-blue' ? 'section--navyBlue' : ''}
                            ${darkMode ? 'darkMode' : ''}
                    `}
                    style={width > 768 ? authoredStyles.section : authoredStyles.mobileSection}
                    bgColor={backgroundColor}
                    bgImg={width > 768 ? props.backgroundImage : props.mobileBackgroundImage}
                    bgOverlay={props.backgroundOverlay}
                    ariaLabel=''>
                    {props.enablePrint &&
                        <Row className='PageSection__printContainer'>
                            <span className='PageSection__printIcon no-print'>
                                <svg className='PageSection__svg' aria-hidden='true' data-prefix='fal'
                                    data-icon='print' onClick={() => window.print()}
                                    role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' data-fa-i2svg=''>
                                    <path fill='currentColor'
                                        d='M432 192h-16v-82.75c0-8.49-3.37-16.62-9.37-22.63L329.37 9.37c-6-6-14.14-9.37-22.63-9.37H126.48C109.64 0 96 14.33 96 32v160H80c-44.18 0-80 35.82-80 80v96c0 8.84 7.16 16 16 16h80v112c0 8.84 7.16 16 16 16h288c8.84 0 16-7.16 16-16V384h80c8.84 0 16-7.16 16-16v-96c0-44.18-35.82-80-80-80zM320 45.25L370.75 96H320V45.25zM128.12 32H288v64c0 17.67 14.33 32 32 32h64v64H128.02l.1-160zM384 480H128v-96h256v96zm96-128H32v-80c0-26.47 21.53-48 48-48h352c26.47 0 48 21.53 48 48v80zm-80-88c-13.25 0-24 10.74-24 24 0 13.25 10.75 24 24 24s24-10.75 24-24c0-13.26-10.75-24-24-24z'></path>
                                </svg>
                            </span>
                        </Row>
                    }
                    

                    <div>
                        {props.title && (
                            <Title
                                tag={titleSize}
                                className='Section__title--subtitle'
                                align={titleVariant}
                                spanTitle={props.subtitle && transformHTMLParserLinks(props.subtitle)}
                                verticalBar={props.showHr && titleSize === 'h2' && titleVariant === 'left'}>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}
                            </Title>
                        )}
                    </div>
                        
                    <div className={props.numCols === 5 ? 'aem-Grid aem-Grid--5' : 'aem-Grid aem-Grid--12'}>
                        {
                            itemsOrder?.map((item: string, index: number) => {
                                
                                return (
                                    <div key={`PageSection-${index}`}
                                        className={`${props.columnClasses ? props.columnClasses[index] : ''}`}
                                        style={index === 0 ? authoredStyles.firstColumn : getAuthoredColumnStyle()}>
                                        {/* This is a single line for a reason, don't change it */}
                                        {/* @ts-ignore */}
                                        <CobGrid isInEditor={props.isInEditor} cqPath={`${props.cqPath}/${item}`} cqItems={getSecChildren(props, item)} cqItemsOrder={props.cqItems[item][':itemsOrder']} singleGrid={props.cqItemsOrder.length <= 1} />
                                    </div>
                                );
                            })
                        }
                    </div>
                    {
                        props.sectionLink && props.sectionLinkLabel && (
                            <Row as='div' className='link-section'>
                                <ButtonWrapper href={props.sectionLink} target={props.target} variant={props.ctaStyle}
                                    icon={props.showChevron ? 'chevron' : undefined}
                                    size='medium'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.sectionLinkLabel))}</ButtonWrapper>
                            </Row>
                        )
                    }

                    {
                        props.disclaimer && (
                            <span className='PageSection__disclaimer'>
                                <div className='PageSection__disclaimer--desktop'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.disclaimer))}</div>
                            </span>
                        )
                    }
                </Section>
            )}
            {
                props.disclaimer && (
                    <span className='PageSection__disclaimer'>
                        <div className='PageSection__disclaimer--mobile'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.disclaimer))}</div>
                    </span>
                )
            }
        </React.Fragment>
    );
};

export default PageSection;
